import React, {
  Component,
  type ComponentClass,
  type ReactNode,
  type SFC,
} from "react";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import type { RouteComponentProps } from "react-router-dom";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  UncontrolledTooltip,
} from "reactstrap";
import { bindActionCreators } from "redux";
import { type InjectedFormProps, reduxForm } from "redux-form";
import ListsView, { LISTS_VIEW_PATH } from "./ListsView";
import { CreateList } from "src/lists/actions/ListActionCreators";
import { ListCreate } from "src/lists/formatters/ListFormatters";
import IListData, { IListDataToList } from "src/lists/forms/IListData";
import ListForm from "src/lists/forms/ListForm";
import type List from "src/lists/objects/List";
import Loader from "src/shared/components/Loader";
import RaangeTooltip from "src/shared/components/RaangeTooltip";
import { FileUploadRendererContext } from "src/shared/forms/formrenderers/FileUploadRenderer";

export const LIST_CREATE_VIEW_PATH: string = "/ls/create";

interface ICreateListViewInnerProps
  extends ICreateListViewReduxFormProps,
    InjectedFormProps<IListData, ICreateListViewReduxFormProps> {}
interface ICreateListViewInnerState {
  createListPromise?: Promise<string>;
  progressEvent?: ProgressEvent;
}
class CreateListViewInner extends Component<
  ICreateListViewInnerProps,
  ICreateListViewInnerState
> {
  private canClosePopup: boolean = true;
  public state = {
    createListPromise: undefined,
    progressEvent: undefined,
  };

  public onSubmit = async (formData: IListData): Promise<void> => {
    try {
      this.canClosePopup = false;

      const list: List = IListDataToList(formData);

      const { CreateList } = this.props;
      const createListPromise: Promise<string> = CreateList(
        list,
        this.onUploadProgress,
      );
      this.setState({
        createListPromise,
      });
      await createListPromise;

      const { history } = this.props;
      history.push(LISTS_VIEW_PATH);
    } catch (ex) {
      // Handled by createListPromise.
    } finally {
      this.canClosePopup = true;

      this.setState({
        progressEvent: undefined,
      });
    }
  };

  public onUploadProgress = (progressEvent: ProgressEvent): void => {
    this.setState({
      progressEvent,
    });
  };

  public onPopupAbort = (): void => {
    if (!this.canClosePopup) {
      return;
    }

    const { history } = this.props;
    history.push(LISTS_VIEW_PATH);
  };

  public render(): ReactNode {
    const { handleSubmit, form, submitting, invalid, pristine } = this.props;
    const { createListPromise, progressEvent } = this.state;
    return (
      <Modal isOpen toggle={this.onPopupAbort} className="list-view-popup">
        <ModalHeader toggle={this.onPopupAbort}>
          <ListCreate />
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit(this.onSubmit)}>
            <FileUploadRendererContext.Provider value={progressEvent}>
              <ListForm form={form} nextButtonDisabled={submitting || invalid}>
                <p>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={pristine || submitting || invalid}
                    className="mr-2"
                  >
                    <FormattedMessage id="lists.create.submit" />
                  </Button>

                  <RaangeTooltip
                    tag="span"
                    tooltip={(target) => (
                      <UncontrolledTooltip placement="right" target={target}>
                        <FormattedMessage id="lists.create.submit.tooltip" />
                      </UncontrolledTooltip>
                    )}
                  >
                    <a href="###">
                      <i className="fas fa-question-circle fa-fw" />
                    </a>
                  </RaangeTooltip>
                </p>

                {createListPromise && <Loader promise={createListPromise} />}
              </ListForm>
            </FileUploadRendererContext.Provider>
          </form>
        </ModalBody>
      </Modal>
    );
  }
}

// Redux Forms
interface ICreateListViewReduxFormProps
  extends ICreateListViewInitialValuesProps {
  initialValues: Partial<IListData>;
}
const CreateListViewReduxForm: ComponentClass<ICreateListViewReduxFormProps> =
  reduxForm<IListData, ICreateListViewReduxFormProps>({
    form: "CreateListView",
  })(CreateListViewInner);

// Initial Values.
interface ICreateListViewInitialValuesProps extends ICreateListViewReduxProps {
  CreateList: (
    list: List,
    onUploadProgress?: (progressEvent: ProgressEvent) => void,
  ) => Promise<string>;
}
const CreateListViewInitialValues: SFC<ICreateListViewInitialValuesProps> = (
  props,
) => {
  const initialValues = {};
  return <CreateListViewReduxForm {...props} initialValues={initialValues} />;
};

// Redux Connect
interface ICreateListViewReduxProps extends ICreateListViewProps {
  onLoad: () => void;
}
const CreateListViewRedux = connect(
  (state, props: ICreateListViewReduxProps) => ({}),
  (list) =>
    bindActionCreators(
      {
        CreateList,
      },
      list,
    ),
)(CreateListViewInitialValues);

// Export Default
export interface ICreateListViewProps extends RouteComponentProps<void> {}
export default (props: ICreateListViewProps) => {
  return (
    <ListsView {...props}>
      {(offers, onLoad) =>
        React.createElement(CreateListViewRedux, {
          ...props,
          onLoad,
        })
      }
    </ListsView>
  );
};
